/**
 * RKG vs Legacy MC Comparison Panel
 * Shows RKG predictions alongside legacy engine with delta indicators
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertTriangle, CheckCircle2, GitCompareArrows, ShieldCheck, Info } from 'lucide-react';
import type { RkgComparisonData, RkgBacktestMetrics } from '@/hooks/useRkgComparison';

interface Props {
  rkgData: RkgComparisonData;
  legacyData?: {
    p50_prazo?: number | null;
    p80_prazo?: number | null;
    prob_atraso?: number | null;
    prob_estouro?: number | null;
  } | null;
  backtestMetrics?: RkgBacktestMetrics | null;
  useFallback: boolean;
}

function delta(rkg: number | null, legacy: number | null): string {
  if (rkg == null || legacy == null || legacy === 0) return '—';
  const d = ((rkg - legacy) / legacy) * 100;
  return `${d > 0 ? '+' : ''}${d.toFixed(1)}%`;
}

function deltaColor(rkg: number | null, legacy: number | null): string {
  if (rkg == null || legacy == null || legacy === 0) return 'text-muted-foreground';
  const d = Math.abs((rkg - legacy) / legacy) * 100;
  if (d <= 5) return 'text-emerald-500';
  if (d <= 15) return 'text-amber-500';
  return 'text-destructive';
}

const divergenceLabels: Record<string, { label: string; variant: 'default' | 'outline' | 'destructive' }> = {
  aligned: { label: 'Alinhado', variant: 'default' },
  moderate: { label: 'Divergência Moderada', variant: 'outline' },
  high_divergence: { label: 'Alta Divergência', variant: 'destructive' },
};

export const RkgComparisonPanel: React.FC<Props> = ({ rkgData, legacyData, backtestMetrics, useFallback }) => {
  const divInfo = divergenceLabels[rkgData.divergence_level || 'moderate'];

  return (
    <Card className="border-primary/20">
      <CardHeader className="py-3 px-5">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <GitCompareArrows className="w-4 h-4 text-primary" />
            Previsão RKG vs Motor Atual
          </CardTitle>
          <div className="flex items-center gap-2">
            {useFallback && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Badge variant="destructive" className="text-xs gap-1">
                      <AlertTriangle className="w-3 h-3" /> Fallback Ativo
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">Divergência {'>'} 30% — motor legado prioritário</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <Badge variant={divInfo.variant} className="text-xs">{divInfo.label}</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-5 pb-4 space-y-4">
        {/* Comparison Grid */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-muted-foreground">
                <th className="text-left py-2 pr-3 font-medium">Métrica</th>
                <th className="text-right py-2 px-3 font-medium">Motor Atual</th>
                <th className="text-right py-2 px-3 font-medium text-primary">Motor RKG</th>
                <th className="text-right py-2 pl-3 font-medium">Delta</th>
              </tr>
            </thead>
            <tbody>
              <ComparisonRow
                label="P50 Prazo (dias)"
                legacy={legacyData?.p50_prazo}
                rkg={rkgData.rkg_p50}
              />
              <ComparisonRow
                label="P80 Prazo (dias)"
                legacy={legacyData?.p80_prazo}
                rkg={rkgData.rkg_p80}
              />
              <ComparisonRow
                label="P90 Prazo (dias)"
                legacy={null}
                rkg={rkgData.rkg_p90}
              />
              <ComparisonRow
                label="Prob. Atraso (%)"
                legacy={legacyData?.prob_atraso}
                rkg={rkgData.rkg_delay_prob}
                suffix="%"
              />
              <ComparisonRow
                label="Prob. Sobrecusto (%)"
                legacy={legacyData?.prob_estouro}
                rkg={rkgData.rkg_cost_overrun_prob}
                suffix="%"
              />
            </tbody>
          </table>
        </div>

        {/* Backtest Metrics */}
        {backtestMetrics && (
          <div className="border-t pt-3">
            <div className="flex items-center gap-1 mb-2">
              <ShieldCheck className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Validação do Modelo</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <MetricBadge
                label="MAPE"
                value={backtestMetrics.mape != null ? `${backtestMetrics.mape.toFixed(1)}%` : '—'}
                ok={backtestMetrics.mape != null && backtestMetrics.mape < 25}
                target="< 25%"
              />
              <MetricBadge
                label="Brier Score"
                value={backtestMetrics.brier_score != null ? backtestMetrics.brier_score.toFixed(3) : '—'}
                ok={backtestMetrics.brier_score != null && backtestMetrics.brier_score < 0.2}
                target="< 0.20"
              />
              <MetricBadge
                label="Coverage P80"
                value={backtestMetrics.coverage_p80 != null ? `${backtestMetrics.coverage_p80.toFixed(0)}%` : '—'}
                ok={backtestMetrics.coverage_p80 != null && backtestMetrics.coverage_p80 >= 75 && backtestMetrics.coverage_p80 <= 85}
                target="75-85%"
              />
              <MetricBadge
                label="Amostra"
                value={backtestMetrics.sample_size?.toString() || '—'}
                ok={backtestMetrics.sample_size != null && backtestMetrics.sample_size >= 30}
                target="≥ 30"
              />
            </div>
          </div>
        )}

        {/* RKG Adjustments */}
        {rkgData.rkg_adjustments && (
          <details className="border-t pt-2">
            <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground flex items-center gap-1">
              <Info className="w-3 h-3" /> Fatores de ajuste RKG
            </summary>
            <pre className="mt-1 text-xs bg-muted/50 p-2 rounded overflow-x-auto">
              {JSON.stringify(rkgData.rkg_adjustments, null, 2)}
            </pre>
          </details>
        )}

        {/* Timestamp */}
        <p className="text-xs text-muted-foreground text-right">
          RKG atualizado em: {rkgData.completed_at ? new Date(rkgData.completed_at).toLocaleString('pt-BR') : '—'}
        </p>
      </CardContent>
    </Card>
  );
};

function ComparisonRow({ label, legacy, rkg, suffix = '' }: {
  label: string; legacy: number | null | undefined; rkg: number | null; suffix?: string;
}) {
  return (
    <tr className="border-b border-border/50 hover:bg-muted/30">
      <td className="py-2 pr-3 text-muted-foreground">{label}</td>
      <td className="py-2 px-3 text-right font-mono">
        {legacy != null ? `${Math.round(legacy)}${suffix}` : '—'}
      </td>
      <td className="py-2 px-3 text-right font-mono font-semibold text-primary">
        {rkg != null ? `${Math.round(rkg)}${suffix}` : '—'}
      </td>
      <td className={`py-2 pl-3 text-right font-mono ${deltaColor(rkg, legacy ?? null)}`}>
        {delta(rkg, legacy ?? null)}
      </td>
    </tr>
  );
}

function MetricBadge({ label, value, ok, target }: {
  label: string; value: string; ok: boolean; target: string;
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 p-2 rounded bg-muted/50">
            {ok ? (
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
            ) : (
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
            )}
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground truncate">{label}</p>
              <p className="text-xs font-semibold">{value}</p>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent><p className="text-xs">Meta: {target}</p></TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default RkgComparisonPanel;
